import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn } from '../lib/utils';
import { pagePopperCollisionPadding } from '../utils/pageWidth';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

// The primitive's own close, not a manual `setOpen(false)`: only this one hands focus back to
// the trigger through Radix's `onCloseAutoFocus`.
const PopoverClose = PopoverPrimitive.Close;

/**
 * Where popovers portal, when not `<body>`. A modal dialog's scroll lock cancels wheel and
 * touch scrolling outside its own content, so a modal surface provides its content node here.
 */
const PopoverContainerContext = React.createContext<HTMLElement | null>(null);

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => {
  const container = React.useContext(PopoverContainerContext);

  return (
    <PopoverPrimitive.Portal container={container ?? undefined}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={pagePopperCollisionPadding()}
        className={cn(
          // The reduced-motion guard carries the data-state too: a bare `motion-reduce:animate-none`
          // is LOWER specificity than `.cls[data-state=open]` and loses, so the entrance ran anyway.
          'z-[3500] w-72 rounded-lg bg-popover p-4 text-popover-foreground shadow-lg outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 motion-reduce:data-[state=closed]:animate-none motion-reduce:data-[state=open]:animate-none',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverClose, PopoverContainerContext, PopoverTrigger, PopoverContent };
